<script>
import validations from './helpers/validations';
import FormInput from 'v3/components/Form/Input.vue';
import { h, reactive, computed, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue'; // GK-HUB: Queue config
// GK-HUB: Auto-resolve per team imports
import DurationInput from 'next/input/DurationInput.vue';
import TextArea from 'next/textarea/TextArea.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

export default {
  components: {
    NextButton,
    FormInput,
    // eslint-disable-next-line vue/no-reserved-component-names
    Switch, // GK-HUB: Queue config toggle
    DurationInput, // GK-HUB: Auto-resolve
    TextArea, // GK-HUB: Auto-resolve
    SingleSelect, // GK-HUB: Auto-resolve
  },
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    submitInProgress: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => {},
    },
    submitButtonText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { t } = useI18n();
    const formData = props.formData || {};
    const {
      description = '',
      name: title = '',
      allow_auto_assign: allowAutoAssign = true,
      // GK-HUB: Queue config fields
      queue_enabled: queueEnabled = true,
      queue_max_per_agent: queueMaxPerAgent = 10,
      queue_distribution_method: queueDistributionMethod = 'least_busy',
      queue_priority_order: queuePriorityOrder = 'priority_and_wait',
      queue_skip_on_break: queueSkipOnBreak = true,
      queue_skip_busy: queueSkipBusy = false,
      queue_notify_agent: queueNotifyAgent = true,
      // GK-HUB: Requeue on logout toggle
      requeue_on_agent_logout: requeueOnAgentLogout = false,
      // GK-HUB: Auto-resolve fields
      auto_resolve_after: autoResolveAfter = null,
      auto_resolve_message: autoResolveMessage = '',
      auto_resolve_ignore_waiting: autoResolveIgnoreWaiting = false,
      auto_resolve_label: autoResolveLabel = null,
      // GK-HUB: end config fields
    } = formData;

    const state = reactive({
      description,
      title,
      allowAutoAssign,
      // GK-HUB: Queue config state
      queueEnabled,
      queueMaxPerAgent,
      queueDistributionMethod,
      queuePriorityOrder,
      queueSkipOnBreak,
      queueSkipBusy,
      queueNotifyAgent,
      requeueOnAgentLogout,
      // GK-HUB: Auto-resolve state
      autoResolveEnabled: !!autoResolveAfter,
      autoResolveAfter,
      autoResolveMessage: autoResolveMessage || '',
      autoResolveIgnoreWaiting: autoResolveIgnoreWaiting || false,
      autoResolveLabelObj: null,
      // GK-HUB: end state
    });

    // GK-HUB: Auto-resolve unit — ref instead of computed so user can change the dropdown
    const detectUnit = val => {
      if (!val) return DURATION_UNITS.MINUTES;
      if (val % (24 * 60) === 0) return DURATION_UNITS.DAYS;
      if (val % 60 === 0) return DURATION_UNITS.HOURS;
      return DURATION_UNITS.MINUTES;
    };
    const autoResolveUnit = ref(detectUnit(autoResolveAfter));

    // GK-HUB: Label options from store
    const labels = useMapGetter('labels/getLabels');
    const labelOptions = computed(() =>
      labels.value?.length
        ? labels.value.map(label => ({
            id: label.title,
            name: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
          }))
        : []
    );

    // Set initial label selection
    if (autoResolveLabel && labelOptions.value?.length) {
      state.autoResolveLabelObj = labelOptions.value.find(
        opt => opt.name === autoResolveLabel
      );
    }

    // GK-HUB: Queue section visible only when auto-assign + queue both enabled
    const showQueueConfig = computed(
      () => state.allowAutoAssign && state.queueEnabled
    );

    const maxPerAgentError = computed(() => {
      const val = Number(state.queueMaxPerAgent);
      // GK-HUB #394: teto 100 (espelha Team::QUEUE_MAX_PER_AGENT_LIMIT)
      if (!Number.isInteger(val) || val < 1 || val > 100) return true;
      return false;
    });

    const rules = validations;
    const v$ = useVuelidate(rules, state);
    return {
      t,
      state,
      v$,
      showQueueConfig,
      maxPerAgentError,
      autoResolveUnit,
      labelOptions,
      DURATION_UNITS,
    };
  },
  methods: {
    toggleAutoResolve() {
      if (!this.state.autoResolveEnabled) {
        this.state.autoResolveAfter = null;
        this.state.autoResolveMessage = '';
        this.state.autoResolveIgnoreWaiting = false;
        this.state.autoResolveLabelObj = null;
      }
    },
    handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      // GK-HUB: Validate max per agent before submit
      if (this.state.queueEnabled && this.maxPerAgentError) {
        return;
      }
      // GK-HUB: Validate auto-resolve duration
      if (
        this.state.autoResolveEnabled &&
        this.state.autoResolveAfter &&
        this.state.autoResolveAfter < 10
      ) {
        useAlert(this.t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
        return;
      }
      this.onSubmit({
        description: this.state.description,
        name: this.state.title,
        allow_auto_assign: this.state.allowAutoAssign,
        // GK-HUB: Queue config payload
        queue_enabled: this.state.queueEnabled,
        queue_max_per_agent: Number(this.state.queueMaxPerAgent),
        queue_distribution_method: this.state.queueDistributionMethod,
        queue_priority_order: this.state.queuePriorityOrder,
        queue_skip_on_break: this.state.queueSkipOnBreak,
        queue_skip_busy: this.state.queueSkipBusy,
        queue_notify_agent: this.state.queueNotifyAgent,
        // GK-HUB: Requeue on logout payload
        requeue_on_agent_logout: this.state.requeueOnAgentLogout,
        // GK-HUB: Auto-resolve payload
        auto_resolve_after: this.state.autoResolveEnabled
          ? this.state.autoResolveAfter
          : null,
        auto_resolve_message: this.state.autoResolveEnabled
          ? this.state.autoResolveMessage
          : '',
        auto_resolve_ignore_waiting: this.state.autoResolveEnabled
          ? this.state.autoResolveIgnoreWaiting
          : false,
        auto_resolve_label: this.state.autoResolveEnabled
          ? (this.state.autoResolveLabelObj?.name ?? null)
          : null,
        // GK-HUB: end payload
      });
    },
  },
};
</script>

<template>
  <div class="flex-shrink-0 w-full">
    <form class="mx-0 grid gap-4" @submit.prevent="handleSubmit">
      <FormInput
        v-model="state.title"
        name="title"
        spacing="compact"
        :label="$t('TEAMS_SETTINGS.FORM.NAME.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
        :has-error="v$.title.$error"
        :error-message="v$.title.$error ? v$.title.$errors[0].$message : ''"
        @blur="v$.title.$touch"
      />
      <FormInput
        v-model="state.description"
        name="description"
        spacing="compact"
        :label="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
        :has-error="v$.description.$error"
        :error-message="
          v$.description.$error ? v$.description.$errors[0].$message : ''
        "
        @blur="v$.description.$touch"
      />
      <div class="w-full flex items-center gap-2">
        <input v-model="state.allowAutoAssign" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t('TEAMS_SETTINGS.FORM.AUTO_ASSIGN.LABEL') }}
        </label>
      </div>

      <!-- GK-HUB: Queue configuration section -->
      <div
        v-if="state.allowAutoAssign"
        class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
      >
        <!-- Header: Queue enabled toggle -->
        <div class="flex flex-col gap-1 items-start px-4 py-3">
          <div class="flex justify-between items-center w-full">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.FORM.QUEUE.TITLE') }}
            </h3>
            <Switch v-model="state.queueEnabled" />
          </div>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.FORM.QUEUE.DESCRIPTION') }}
          </p>
        </div>

        <!-- Queue config fields (visible when queue enabled) -->
        <div v-if="showQueueConfig" class="px-4 py-3">
          <div class="grid gap-4">
            <!-- Row 1: Limit + Distribution method -->
            <div class="grid grid-cols-2 gap-3">
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-12">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.MAX_PER_AGENT.LABEL') }}
                </label>
                <input
                  v-model.number="state.queueMaxPerAgent"
                  type="number"
                  min="1"
                  max="100"
                  class="block w-full border-none rounded-md shadow-sm bg-n-alpha-black2 outline outline-1 text-n-slate-12 placeholder:text-n-slate-10 sm:text-sm sm:leading-6 px-3 py-2"
                  :class="
                    maxPerAgentError
                      ? 'outline-n-ruby-8 hover:outline-n-ruby-9'
                      : 'outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand'
                  "
                />
                <span v-if="maxPerAgentError" class="text-xs text-n-ruby-9">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.MAX_PER_AGENT.ERROR') }}
                </span>
                <span v-else class="text-xs text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.MAX_PER_AGENT.HELP') }}
                </span>
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-12">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.DISTRIBUTION.LABEL') }}
                </label>
                <select
                  v-model="state.queueDistributionMethod"
                  class="block w-full border-none rounded-md shadow-sm bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand text-n-slate-12 sm:text-sm sm:leading-6 px-3 py-2 appearance-none queue-select-arrow"
                >
                  <option value="least_busy">
                    {{
                      $t('TEAMS_SETTINGS.FORM.QUEUE.DISTRIBUTION.LEAST_BUSY')
                    }}
                  </option>
                  <option value="round_robin">
                    {{
                      $t('TEAMS_SETTINGS.FORM.QUEUE.DISTRIBUTION.ROUND_ROBIN')
                    }}
                  </option>
                </select>
                <span class="text-xs text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.DISTRIBUTION.HELP') }}
                </span>
              </div>
            </div>

            <!-- Row 2: Priority order -->
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-12">
                {{ $t('TEAMS_SETTINGS.FORM.QUEUE.PRIORITY.LABEL') }}
              </label>
              <select
                v-model="state.queuePriorityOrder"
                class="block w-full border-none rounded-md shadow-sm bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand text-n-slate-12 sm:text-sm sm:leading-6 px-3 py-2 appearance-none queue-select-arrow"
              >
                <option value="priority_and_wait">
                  {{
                    $t('TEAMS_SETTINGS.FORM.QUEUE.PRIORITY.PRIORITY_AND_WAIT')
                  }}
                </option>
                <option value="fifo">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.PRIORITY.FIFO') }}
                </option>
                <option value="priority_only">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.PRIORITY.PRIORITY_ONLY') }}
                </option>
              </select>
              <span class="text-xs text-n-slate-10">
                {{ $t('TEAMS_SETTINGS.FORM.QUEUE.PRIORITY.HELP') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Rules section (visible when queue enabled) -->
        <div v-if="showQueueConfig" class="px-4 py-3">
          <h4 class="text-xs font-medium text-n-slate-11 mb-2">
            {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.TITLE') }}
          </h4>
          <div
            class="rounded-lg border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
          >
            <div class="px-3 py-2.5 flex items-center justify-between">
              <div class="flex flex-col">
                <span class="text-sm">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.SKIP_ON_BREAK') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.SKIP_ON_BREAK_HELP') }}
                </span>
              </div>
              <Switch v-model="state.queueSkipOnBreak" />
            </div>
            <div class="px-3 py-2.5 flex items-center justify-between">
              <div class="flex flex-col">
                <span class="text-sm">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.SKIP_BUSY') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.SKIP_BUSY_HELP') }}
                </span>
              </div>
              <Switch v-model="state.queueSkipBusy" />
            </div>
            <div class="px-3 py-2.5 flex items-center justify-between">
              <div class="flex flex-col">
                <span class="text-sm">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.NOTIFY_AGENT') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.NOTIFY_AGENT_HELP') }}
                </span>
              </div>
              <Switch v-model="state.queueNotifyAgent" />
            </div>
            <!-- GK-HUB: Redistribute conversations on agent logout -->
            <div class="px-3 py-2.5 flex items-center justify-between">
              <div class="flex flex-col">
                <span class="text-sm">
                  {{ $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.REQUEUE_ON_LOGOUT') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{
                    $t('TEAMS_SETTINGS.FORM.QUEUE.RULES.REQUEUE_ON_LOGOUT_HELP')
                  }}
                </span>
              </div>
              <Switch v-model="state.requeueOnAgentLogout" />
            </div>
            <!-- GK-HUB: end requeue on logout -->
          </div>
        </div>
      </div>
      <!-- GK-HUB: end queue configuration section -->

      <!-- GK-HUB: Auto-resolve per team section -->
      <div
        class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
      >
        <div class="flex flex-col gap-1 items-start px-4 py-3">
          <div class="flex justify-between items-center w-full">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE') }}
            </h3>
            <Switch
              v-model="state.autoResolveEnabled"
              @change="toggleAutoResolve"
            />
          </div>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TEAM_NOTE') }}
          </p>
          <p
            v-if="!state.autoResolveEnabled"
            class="mb-0 text-xs text-n-slate-10 italic"
          >
            {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.INHERIT') }}
          </p>
        </div>

        <div v-if="state.autoResolveEnabled" class="px-4 py-3">
          <div class="grid gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-12">
                {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL') }}
              </label>
              <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
                <DurationInput
                  v-model="state.autoResolveAfter"
                  v-model:unit="autoResolveUnit"
                  min="0"
                  max="1438560"
                  class="w-full"
                />
              </div>
              <span class="text-xs text-n-slate-10">
                {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP') }}
              </span>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-12">
                {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL') }}
              </label>
              <TextArea
                v-model="state.autoResolveMessage"
                class="w-full"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
                "
              />
              <span class="text-xs text-n-slate-10">
                {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.HELP') }}
              </span>
            </div>

            <div
              class="rounded-lg border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
            >
              <div class="px-3 py-2.5 flex items-center justify-between">
                <span>
                  {{
                    $t(
                      'GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL'
                    )
                  }}
                </span>
                <Switch v-model="state.autoResolveIgnoreWaiting" />
              </div>
              <div class="px-3 py-2.5 flex items-center justify-between">
                <span>
                  {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}
                </span>
                <SingleSelect
                  v-model="state.autoResolveLabelObj"
                  :options="labelOptions"
                  :placeholder="
                    $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
                  "
                  placeholder-icon="i-lucide-chevron-down"
                  placeholder-trailing-icon
                  variant="faded"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- GK-HUB: end auto-resolve section -->

      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <div class="w-full">
          <NextButton
            type="submit"
            :label="submitButtonText"
            :disabled="
              v$.title.$invalid ||
              submitInProgress ||
              (state.queueEnabled && maxPerAgentError)
            "
            :is-loading="submitInProgress"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<!-- GK-HUB: Queue select arrow style (moved from inline to avoid vue/no-static-inline-styles) -->
<style scoped>
.queue-select-arrow {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238E97AD' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
}
</style>
